import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Box,
  Fab,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Button,
  Menu,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  ThermostatAuto as TempIcon,
  WaterDrop as HumidityIcon,
  Window as WindowIcon,
  Wifi as WifiIcon,
  WifiOff as WifiOffIcon,
  AccessTime as TimeIcon,
  LocalFireDepartment as FlameIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useHomes } from '../context/HomesContext';
import axios from 'axios';
import { format } from 'date-fns';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { homes, selectedHomeId, setSelectedHomeId, loadHomes: loadHomesContext } = useHomes();
  const [readings, setReadings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [homeStatus, setHomeStatus] = useState(null);
  const [modeAnchor, setModeAnchor] = useState(null);
  const [modeContext, setModeContext] = useState(null); // { homeId, roomId }
  const [manualOpen, setManualOpen] = useState(false);
  const [manualTemp, setManualTemp] = useState(20);
  const [manualDuration, setManualDuration] = useState(60); // minutes
  const [maxOpen, setMaxOpen] = useState(false);
  const [maxDuration, setMaxDuration] = useState(30);
  const [homeDurOpen, setHomeDurOpen] = useState(false);
  const [homeDurMinutes, setHomeDurMinutes] = useState('');
  const [homeModePending, setHomeModePending] = useState(null); // 'away' | 'frostguard'
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [renameTarget, setRenameTarget] = useState(null); // { type: 'home'|'room', homeId, roomId? }
  const [schedulesOpen, setSchedulesOpen] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [activeScheduleId, setActiveScheduleId] = useState('');
  const [renamingSchedule, setRenamingSchedule] = useState(null); // { id, name }
  const [newScheduleName, setNewScheduleName] = useState('');
  const [editingSchedule, setEditingSchedule] = useState(null); // { id, name, zones, timetable }

  const fetchReadings = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError('');
      
      const response = await axios.get('/api/heat/readings?limit=20');
      setReadings(response.data.readings);
    } catch (error) {
      console.error('Failed to fetch readings:', error);
      setError('Failed to load readings. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleDeleteReading = async (id) => {
    if (!confirm('Are you sure you want to delete this reading?')) {
      return;
    }

    try {
      await axios.delete(`/api/heat/readings/${id}`);
      setReadings(prev => prev.filter(reading => reading.id !== id));
    } catch (error) {
      console.error('Failed to delete reading:', error);
      setError('Failed to delete reading. Please try again.');
    }
  };

  useEffect(() => {
    fetchReadings();
    // Check for Netatmo callback status
    const params = new URLSearchParams(window.location.search);
    if (params.get('netatmo') === 'success') {
      setError('');
      // Clear the query param
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('netatmo') === 'error') {
      setError('Netatmo connection failed');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const connectNetatmo = async () => {
    try {
      const res = await axios.get('/api/netatmo/authorize-url');
      window.location.href = res.data.url;
    } catch (e) {
      console.error(e);
      setError('Failed to start Netatmo authorization');
    }
  };

  const loadHomes = async () => {
    try {
      await loadHomesContext();
      setError('');
    } catch (e) {
      console.error(e);
      setError('Failed to load Netatmo homes');
    }
  };

  const loadHomeStatus = async (homeId) => {
    try {
      const res = await axios.get(`/api/netatmo/homestatus/${homeId}`);
      setHomeStatus(res.data.status || null);
      setError('');
    } catch (e) {
      console.error(e);
      setError('Failed to load Netatmo home status');
    }
  };

  const openModeMenu = (event, homeId, roomId) => {
    setModeAnchor(event.currentTarget);
    setModeContext({ homeId, roomId });
  };
  const closeModeMenu = () => {
    setModeAnchor(null);
    setModeContext(null);
  };

  const openManualDialog = () => {
    if (!modeContext) return;
    setManualTemp(20);
    setManualDuration(60);
    setManualOpen(true);
  };
  const closeManualDialog = () => setManualOpen(false);
  const submitManualDialog = async () => {
    if (!modeContext) return;
    const t = parseFloat(manualTemp);
    const d = parseInt(manualDuration, 10);
    if (Number.isNaN(t)) {
      setError('Please enter a valid temperature');
      return;
    }
    const tempClamped = Math.max(5, Math.min(30, t));
    const durationValid = !Number.isNaN(d) && d > 0 ? d : undefined;
    try {
      const payload = { home_id: modeContext.homeId, room_id: modeContext.roomId, mode: 'manual', temp: tempClamped };
      if (durationValid) payload.duration_minutes = durationValid;
      await axios.post('/api/netatmo/setroomthermpoint', payload);
      await loadHomeStatus(modeContext.homeId);
      closeManualDialog();
      closeModeMenu();
    } catch (e) {
      console.error(e);
      setError('Failed to set manual mode');
    }
  };

  const setHomeMode = async (mode) => {
    if (!selectedHomeId) return;
    try {
      if (mode === 'away' || mode === 'frostguard') {
        setHomeModePending(mode);
        setHomeDurMinutes('');
        setHomeDurOpen(true);
        return;
      }
      const payload = { home_id: selectedHomeId, mode };
      await axios.post('/api/netatmo/setthermmode', payload);
      await loadHomeStatus(selectedHomeId);
      closeModeMenu();
    } catch (e) {
      console.error(e);
      setError('Failed to set home mode');
    }
  };

  const setRoomMode = async (mode, temp) => {
    if (!modeContext) return;
    try {
      // Manual handled via dialog
      if (mode === 'manual') {
        openManualDialog();
        return;
      }
      if (mode === 'max') {
        setMaxDuration(30);
        setMaxOpen(true);
        return;
      }
      const payload = { home_id: modeContext.homeId, room_id: modeContext.roomId, mode };
      await axios.post('/api/netatmo/setroomthermpoint', payload);
      await loadHomeStatus(modeContext.homeId);
      closeModeMenu();
    } catch (e) {
      console.error(e);
      setError('Failed to set room mode');
    }
  };

  const closeMaxDialog = () => setMaxOpen(false);
  const submitMaxDialog = async () => {
    if (!modeContext) return;
    const d = parseInt(maxDuration, 10);
    const payload = { home_id: modeContext.homeId, room_id: modeContext.roomId, mode: 'max' };
    if (!Number.isNaN(d) && d > 0) payload.duration_minutes = d;
    try {
      await axios.post('/api/netatmo/setroomthermpoint', payload);
      await loadHomeStatus(modeContext.homeId);
      closeMaxDialog();
      closeModeMenu();
    } catch (e) {
      console.error(e);
      setError('Failed to set max mode');
    }
  };

  const closeHomeDurDialog = () => setHomeDurOpen(false);
  const submitHomeDurDialog = async () => {
    if (!selectedHomeId || !homeModePending) return;
    const d = parseInt(homeDurMinutes, 10);
    const payload = { home_id: selectedHomeId, mode: homeModePending };
    if (!Number.isNaN(d) && d > 0) payload.duration_minutes = d;
    try {
      await axios.post('/api/netatmo/setthermmode', payload);
      await loadHomeStatus(selectedHomeId);
      closeHomeDurDialog();
      closeModeMenu();
      setHomeModePending(null);
    } catch (e) {
      console.error(e);
      setError('Failed to set home mode');
    }
  };

  // When selectedHomeId changes, load its status
  useEffect(() => {
    if (selectedHomeId) {
      loadHomeStatus(selectedHomeId);
    } else {
      setHomeStatus(null);
    }
  }, [selectedHomeId]);

  // Auto-load homes on initial page load
  useEffect(() => {
    loadHomes();
  }, []);

  const formatTemperature = (temp) => {
    return `${temp.toFixed(1)}°C`;
  };

  const formatHumidity = (humidity) => {
    return humidity ? `${humidity.toFixed(1)}%` : 'N/A';
  };

  const formatTimestamp = (timestamp) => {
    return format(new Date(timestamp), 'MMM dd, HH:mm');
  };

  const openRename = (target, currentName) => {
    setRenameTarget(target);
    setRenameValue(currentName || '');
    setRenameOpen(true);
  };
  const closeRename = () => setRenameOpen(false);
  const submitRename = async () => {
    try {
      if (!renameTarget || !user?.isAdmin) return;
      if (renameTarget.type === 'home') {
        await axios.post('/api/netatmo/home-alias', { home_id: renameTarget.homeId, alias: renameValue });
      } else {
        await axios.post('/api/netatmo/room-alias', { home_id: renameTarget.homeId, room_id: renameTarget.roomId, alias: renameValue });
      }
      await loadHomes();
      if (selectedHomeId) await loadHomeStatus(selectedHomeId);
      closeRename();
    } catch (e) {
      console.error(e);
      setError('Failed to rename');
    }
  };

  // Schedules
  const openSchedulesDialog = async (homeId) => {
    try {
      const res = await axios.get(`/api/netatmo/schedules/${homeId}`);
      setSchedules(res.data.schedules || []);
      setActiveScheduleId(res.data.activeScheduleId || '');
      setSchedulesOpen(true);
    } catch (e) {
      console.error(e);
      setError('Failed to load schedules');
    }
  };

  const activateSchedule = async (scheduleId) => {
    try {
      await axios.post('/api/netatmo/switchhomeschedule', { home_id: selectedHomeId, schedule_id: scheduleId });
      setActiveScheduleId(scheduleId);
    } catch (e) {
      console.error(e);
      setError('Failed to activate schedule');
    }
  };

  const startRenameSchedule = (schedule) => {
    setRenamingSchedule(schedule);
    setNewScheduleName(schedule.name || '');
  };

  const submitRenameSchedule = async () => {
    if (!renamingSchedule || !user?.isAdmin) return;
    try {
      await axios.post('/api/netatmo/renamehomeschedule', { home_id: selectedHomeId, schedule_id: renamingSchedule.id, name: newScheduleName });
      // update local list
      setSchedules((prev) => prev.map(s => s.id === renamingSchedule.id ? { ...s, name: newScheduleName } : s));
      setRenamingSchedule(null);
    } catch (e) {
      console.error(e);
      setError('Failed to rename schedule');
    }
  };

  const startEditSchedule = async (scheduleSummary) => {
    try {
      const res = await axios.get(`/api/netatmo/schedule/${selectedHomeId}/${scheduleSummary.id}`);
      setEditingSchedule(res.data.schedule);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.detail || 'Failed to load schedule details');
    }
  };

  const updateZoneName = (index, name) => {
    setEditingSchedule((prev) => {
      if (!prev) return prev;
      const zones = [...(prev.zones || [])];
      zones[index] = { ...zones[index], name };
      return { ...prev, zones };
    });
  };

  const updateTimetable = (index, patch) => {
    setEditingSchedule((prev) => {
      if (!prev) return prev;
      const tt = [...(prev.timetable || [])];
      tt[index] = { ...tt[index], ...patch };
      return { ...prev, timetable: tt };
    });
  };

  const updateZoneRoomTemp = (zoneIndex, roomId, tempVal) => {
    setEditingSchedule((prev) => {
      if (!prev) return prev;
      const zones = [...(prev.zones || [])];
      const z = { ...zones[zoneIndex] };
      const list = [...(z.rooms_temp || [])];
      const rid = roomId;
      const idx = list.findIndex(rt => (rt.room_id ?? rt.id) === rid);
      const num = tempVal === '' ? '' : parseFloat(tempVal);
      if (idx >= 0) {
        const cur = list[idx];
        list[idx] = { room_id: cur.room_id ?? cur.id, temp: tempVal === '' ? '' : num };
      }
      z.rooms_temp = list;
      zones[zoneIndex] = z;
      return { ...prev, zones };
    });
  };

  const submitEditSchedule = async () => {
    if (!editingSchedule || !user?.isAdmin) return;
    try {
      await axios.post('/api/netatmo/synchomeschedule', {
        home_id: selectedHomeId,
        schedule_id: editingSchedule.id,
        name: editingSchedule.name,
        away_temp: editingSchedule.away_temp,
        hg_temp: editingSchedule.hg_temp,
        zones: editingSchedule.zones || [],
        timetable: editingSchedule.timetable || [],
      });
      setEditingSchedule(null);
      // refresh schedules list to reflect potential changes
      await openSchedulesDialog(selectedHomeId);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.detail || 'Failed to save schedule');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ pt: 3 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ pt: 3, pb: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" component="h1" fontWeight="bold">
          Homes
        </Typography>
        <IconButton 
          onClick={() => fetchReadings(true)} 
          disabled={refreshing}
          color="primary"
        >
          <RefreshIcon />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Netatmo connect and homes dropdown */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Netatmo Energy</Typography>
            <Box display="flex" gap={1}>
              {user?.isAdmin && (
                <Button size="small" variant="outlined" onClick={connectNetatmo}>Connect</Button>
              )}
              <Button size="small" variant="contained" onClick={loadHomes}>Load Homes</Button>
              {selectedHomeId && (
                <Button size="small" variant="outlined" onClick={() => openSchedulesDialog(selectedHomeId)}>Schedules</Button>
              )}
            </Box>
          </Box>
          {homes.length > 0 && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="home-select-label">Select Home</InputLabel>
              <Select
                labelId="home-select-label"
                value={selectedHomeId}
                label="Select Home"
                onChange={e => setSelectedHomeId(e.target.value)}
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {homes.map((h) => (
                  <MenuItem key={h.id} value={h.id}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <span>{h.alias || h.name || 'Unnamed Home'}</span>
                      {user?.isAdmin && (
                        <Button size="small" onClick={(e) => { e.stopPropagation(); openRename({ type: 'home', homeId: h.id }, h.alias || h.name); }}>Rename</Button>
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {/* Show rooms and temperatures for selected home */}
          {(() => {
            const rooms = homeStatus?.home?.rooms || homeStatus?.rooms || [];
            if (!rooms.length) return null;
            // Map room names from the selected home in homes list
            const selectedHome = homes.find(h => h.id === selectedHomeId);
            const nameById = new Map((selectedHome?.rooms || []).map(r => [r.id, r.alias || r.name]));
            return (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Rooms & Temperatures</Typography>
                <List>
                  {rooms.map((room) => {
                    const name = nameById.get(room.id) || room.alias || room.name || `Room ${room.id}`;
                    const measured = room.therm_measured_temperature != null ? `${room.therm_measured_temperature.toFixed(1)}°C` : 'N/A';
                    const setpoint = room.therm_setpoint_temperature != null ? `${room.therm_setpoint_temperature.toFixed(1)}°C` : 'N/A';
                    const mode = room.therm_setpoint_mode || 'unknown';
                    const windowOpen = !!room.open_window;
                    const reachable = room.reachable !== false; // default true if missing
                    const anticipating = !!room.anticipating;
                    const power = typeof room.heating_power_request === 'number' ? Math.max(0, Math.min(100, room.heating_power_request)) : 0;
                    const powerColor = power === 0 ? 'default' : power <= 33 ? 'info' : power <= 66 ? 'warning' : 'error';
                    return (
                      <ListItem 
                        key={room.id} 
                        alignItems="flex-start"
                        sx={{ 
                          flexDirection: 'column',
                          gap: 2,
                          py: 2,
                          borderBottom: 1,
                          borderColor: 'divider'
                        }}
                      >
                        <Box width="100%" display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="h6" component="div" fontWeight="600">{name}</Typography>
                          <Box display="flex" gap={1} flexShrink={0}>
                            {user?.isAdmin && (
                              <Button size="small" onClick={(e) => { e.stopPropagation(); openRename({ type: 'room', homeId: selectedHomeId, roomId: room.id }, name); }}>Rename</Button>
                            )}
                            <Button size="small" variant="outlined" onClick={(e) => openModeMenu(e, selectedHomeId, room.id)}>Mode</Button>
                          </Box>
                        </Box>
                        <Box width="100%">
                          {/* Big temperatures row */}
                          <Box display="flex" gap={3} alignItems="baseline" mb={1} flexWrap="wrap">
                            <Box display="flex" alignItems="baseline" gap={1}>
                              <TempIcon fontSize="small" />
                              <Typography variant="body2" color="text.secondary">Measured</Typography>
                              <Typography variant="h5" component="span" fontWeight="bold">{measured}</Typography>
                            </Box>
                            <Box display="flex" alignItems="baseline" gap={1}>
                              <TempIcon fontSize="small" color="primary" />
                              <Typography variant="body2" color="text.secondary">Set</Typography>
                              <Typography variant="h5" component="span" fontWeight="bold" color="primary.main">{setpoint}</Typography>
                            </Box>
                          </Box>
                          {/* Status chips row */}
                          <Box display="flex" flexWrap="wrap" gap={1} mt={0.5}>
                            <Chip icon={<TimeIcon />} label={`Mode ${mode}`} size="small" />
                            <Chip icon={<WindowIcon />} label={windowOpen ? 'Window Open' : 'Window Closed'} size="small" color={windowOpen ? 'warning' : 'default'} />
                            <Chip icon={reachable ? <WifiIcon /> : <WifiOffIcon />} label={reachable ? 'Online' : 'Offline'} size="small" color={reachable ? 'success' : 'error'} />
                            <Chip icon={<TimeIcon />} label={anticipating ? 'Anticipating' : 'No Anticipation'} size="small" color={anticipating ? 'info' : 'default'} />
                            <Chip icon={<FlameIcon />} label={`Power ${power}%`} size="small" color={powerColor} />
                          </Box>
                        </Box>
                      </ListItem>
                    );
                  })}
                </List>
              </Box>
            );
          })()}
          {/* Mode menu */}
          <Menu
            anchorEl={modeAnchor}
            open={Boolean(modeAnchor)}
            onClose={closeModeMenu}
          >
            {/* Home modes */}
            <MenuItem onClick={() => setHomeMode('schedule')}>Home: Schedule</MenuItem>
            <MenuItem onClick={() => setHomeMode('away')}>Home: Away</MenuItem>
            <MenuItem onClick={() => setHomeMode('frostguard')}>Home: Frostguard</MenuItem>
            <MenuItem disabled divider>Room modes</MenuItem>
            <MenuItem onClick={() => setRoomMode('manual')}>Room: Manual…</MenuItem>
            <MenuItem onClick={() => setRoomMode('max')}>Room: Max</MenuItem>
            <MenuItem onClick={() => setRoomMode('home')}>Room: Home</MenuItem>
          </Menu>

          {/* Manual mode dialog */}
          <Dialog open={manualOpen} onClose={closeManualDialog} fullWidth maxWidth="xs">
            <DialogTitle>Set Manual Mode</DialogTitle>
            <DialogContent>
              <Box display="flex" flexDirection="column" gap={2} mt={1}>
                <TextField
                  label="Temperature (°C)"
                  type="number"
                  inputProps={{ step: 0.5, min: 5, max: 30 }}
                  value={manualTemp}
                  onChange={(e) => setManualTemp(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Duration (minutes)"
                  type="number"
                  helperText="Leave empty for indefinite"
                  inputProps={{ min: 1 }}
                  value={manualDuration}
                  onChange={(e) => setManualDuration(e.target.value)}
                  fullWidth
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeManualDialog}>Cancel</Button>
              <Button variant="contained" onClick={submitManualDialog}>Set</Button>
            </DialogActions>
          </Dialog>
          {/* Max mode dialog */}
          <Dialog open={maxOpen} onClose={closeMaxDialog} fullWidth maxWidth="xs">
            <DialogTitle>Set Max Mode</DialogTitle>
            <DialogContent>
              <Box display="flex" flexDirection="column" gap={2} mt={1}>
                <TextField
                  label="Duration (minutes)"
                  type="number"
                  helperText="Leave empty for default"
                  inputProps={{ min: 1 }}
                  value={maxDuration}
                  onChange={(e) => setMaxDuration(e.target.value)}
                  fullWidth
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeMaxDialog}>Cancel</Button>
              <Button variant="contained" onClick={submitMaxDialog}>Set</Button>
            </DialogActions>
          </Dialog>

          {/* Home Away/Frostguard duration dialog */}
          <Dialog open={homeDurOpen} onClose={closeHomeDurDialog} fullWidth maxWidth="xs">
            <DialogTitle>Set {homeModePending === 'frostguard' ? 'Frostguard' : 'Away'} Mode</DialogTitle>
            <DialogContent>
              <Box display="flex" flexDirection="column" gap={2} mt={1}>
                <TextField
                  label="Duration (minutes)"
                  type="number"
                  helperText="Leave empty for indefinite"
                  inputProps={{ min: 1 }}
                  value={homeDurMinutes}
                  onChange={(e) => setHomeDurMinutes(e.target.value)}
                  fullWidth
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeHomeDurDialog}>Cancel</Button>
              <Button variant="contained" onClick={submitHomeDurDialog}>Set</Button>
            </DialogActions>
          </Dialog>

          {/* Rename dialog */}
          <Dialog open={renameOpen} onClose={closeRename} fullWidth maxWidth="xs">
            <DialogTitle>Rename {renameTarget?.type === 'home' ? 'Home' : 'Room'}</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                label="New name"
                type="text"
                fullWidth
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={closeRename}>Cancel</Button>
              <Button variant="contained" onClick={submitRename}>Save</Button>
            </DialogActions>
          </Dialog>

          {/* Schedules dialog */}
          <Dialog open={schedulesOpen} onClose={() => setSchedulesOpen(false)} fullWidth maxWidth="sm">
            <DialogTitle>Schedules</DialogTitle>
            <DialogContent>
              {schedules.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No schedules found.</Typography>
              ) : (
                <List>
                  {schedules.map((s) => (
                    <ListItem key={s.id}
                      secondaryAction={
                        <Box display="flex" gap={1}>
                          {user?.isAdmin && (
                            <Button size="small" onClick={() => startRenameSchedule(s)}>Rename</Button>
                          )}
                          {user?.isAdmin && (
                            <Button size="small" onClick={() => startEditSchedule(s)}>Edit</Button>
                          )}
                          {activeScheduleId !== s.id && (
                            <Button size="small" variant="outlined" onClick={() => activateSchedule(s.id)}>Activate</Button>
                          )}
                        </Box>
                      }
                    >
                      <ListItemText
                        primary={<Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body1" fontWeight={s.id === activeScheduleId ? 'bold' : 'normal'}>
                            {s.name || s.id}
                          </Typography>
                          {s.id === activeScheduleId && <Chip label="Active" size="small" color="success" />}
                        </Box>}
                        secondary={s.timetable ? `${s.timetable?.length || 0} entries` : null}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSchedulesOpen(false)}>Close</Button>
            </DialogActions>
          </Dialog>

          {/* Rename schedule dialog */}
          <Dialog open={!!renamingSchedule} onClose={() => setRenamingSchedule(null)} fullWidth maxWidth="xs">
            <DialogTitle>Rename Schedule</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                label="New name"
                type="text"
                fullWidth
                value={newScheduleName}
                onChange={(e) => setNewScheduleName(e.target.value)}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setRenamingSchedule(null)}>Cancel</Button>
              <Button variant="contained" onClick={submitRenameSchedule}>Save</Button>
            </DialogActions>
          </Dialog>

          {/* Edit schedule dialog (basic) */}
          <Dialog open={!!editingSchedule} onClose={() => setEditingSchedule(null)} fullWidth maxWidth="md">
            <DialogTitle>Edit Schedule</DialogTitle>
            <DialogContent>
              {!editingSchedule ? null : (
                <Box display="flex" flexDirection="column" gap={2} mt={1}>
                  <TextField
                    label="Schedule name"
                    value={editingSchedule.name || ''}
                    onChange={(e) => setEditingSchedule(prev => ({ ...prev, name: e.target.value }))}
                    fullWidth
                  />
                  <TextField
                    label="Away temperature (°C)"
                    type="number"
                    inputProps={{ step: 0.5, min: 5, max: 30 }}
                    value={editingSchedule.away_temp ?? ''}
                    onChange={(e) => setEditingSchedule(prev => ({ ...prev, away_temp: e.target.value === '' ? null : parseFloat(e.target.value) }))}
                    fullWidth
                  />
                  <TextField
                    label="Frostguard (hg) temperature (°C)"
                    type="number"
                    inputProps={{ step: 0.5, min: 5, max: 30 }}
                    value={editingSchedule.hg_temp ?? ''}
                    onChange={(e) => setEditingSchedule(prev => ({ ...prev, hg_temp: e.target.value === '' ? null : parseFloat(e.target.value) }))}
                    fullWidth
                  />
                  <Typography variant="subtitle2">Zones</Typography>
                  {(() => {
                    const selectedHome = homes.find(h => h.id === selectedHomeId);
                    const roomNameById = new Map((selectedHome?.rooms || []).map(r => [r.id, r.alias || r.name]));
                    return (editingSchedule.zones || []).map((z, idx) => (
                      <Box key={idx} display="flex" flexDirection="column" gap={1} p={1} border={1} borderColor="divider" borderRadius={1}>
                        <Box display="flex" gap={1} alignItems="center">
                          <TextField
                            label={`Zone ${z.type} name`}
                            value={z.name || ''}
                            onChange={(e) => updateZoneName(idx, e.target.value)}
                            size="small"
                          />
                          <Chip label={`type=${z.type}`} size="small" />
                        </Box>
                        <Box display="flex" flexWrap="wrap" gap={1}>
                          {(z.rooms_temp || []).map((rt, i) => {
                            const rId = rt.room_id ?? rt.id;
                            const rName = roomNameById.get(rId) || `Room ${rId}`;
                            return (
                              <TextField
                                key={`${idx}-${rId}`}
                                label={`${rName} (°C)`}
                                type="number"
                                inputProps={{ step: 0.5, min: 5, max: 30 }}
                                value={rt.temp ?? ''}
                                onChange={(e) => updateZoneRoomTemp(idx, rId, e.target.value)}
                                size="small"
                                sx={{ width: 150 }}
                              />
                            );
                          })}
                        </Box>
                      </Box>
                    ));
                  })()}
                  <Typography variant="subtitle2" mt={2}>Timetable</Typography>
                  <Box sx={{ maxHeight: 320, overflowY: 'auto', pr: 1 }}>
                    {(() => {
                      const dayNames = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
                      const toLabel = (mins) => {
                        const d = Math.floor(mins / 1440) % 7;
                        const m = mins % 1440;
                        const hh = String(Math.floor(m / 60)).padStart(2, '0');
                        const mm = String(m % 60).padStart(2, '0');
                        return `${dayNames[d]} ${hh}:${mm}`;
                      };
                      const zones = editingSchedule.zones || [];
                      const avgTemp = (z) => {
                        const vals = (z?.rooms_temp || [])
                          .map(rt => typeof rt.temp === 'number' ? rt.temp : null)
                          .filter(v => v != null);
                        if (!vals.length) return null;
                        const avg = vals.reduce((a,b) => a+b, 0) / vals.length;
                        return Math.round(avg * 10) / 10;
                      };
                      const items = editingSchedule.timetable || [];
                      // Helper to get a numeric zone id if present
                      const getZoneId = (zone, fallbackIdx) => {
                        if (zone && typeof zone.id === 'number') return zone.id;
                        if (zone && typeof zone.zone_id === 'number') return zone.zone_id;
                        return fallbackIdx;
                      };
                      return items.map((t, idx) => {
                        const start = toLabel(t.m_offset);
                        const endOffset = idx < items.length - 1 ? items[idx+1].m_offset : 7 * 24 * 60; // 10080
                        const end = toLabel(endOffset);
                        const z = zones.find(zz => getZoneId(zz) === t.zone_id) || null;
                        const zName = z?.name || `Zone ${t.zone_id}`;
                        const zAvg = avgTemp(z);
                        return (
                          <Box key={idx} display="flex" alignItems="center" gap={2} mb={1}>
                            <Box sx={{ minWidth: 220 }}>
                              <Typography variant="body2" fontWeight={600}>Start: {start}</Typography>
                              <Typography variant="caption" color="text.secondary">End: {end}</Typography>
                            </Box>
                            <FormControl size="small" sx={{ minWidth: 200 }}>
                              <InputLabel id={`tt-zone-${idx}`}>Zone</InputLabel>
                              <Select
                                labelId={`tt-zone-${idx}`}
                                label="Zone"
                                value={t.zone_id}
                                onChange={(e) => {
                                  const val = Number(e.target.value);
                                  updateTimetable(idx, { zone_id: Number.isNaN(val) ? t.zone_id : val });
                                }}
                              >
                                {zones.map((zone, zIdx) => {
                                  const zid = getZoneId(zone, zIdx);
                                  const zavg = avgTemp(zone);
                                  return (
                                    <MenuItem key={zid} value={zid}>
                                      {zone?.name || `Zone ${zid}`} {typeof zavg === 'number' ? `(${zavg}°C)` : ''}
                                    </MenuItem>
                                  );
                                })}
                              </Select>
                            </FormControl>
                            <Box sx={{ minWidth: 100 }}>
                              <Typography variant="body2" color="text.secondary">
                                {typeof zAvg === 'number' ? `Avg ${zAvg}°C` : 'Temp varies'}
                              </Typography>
                            </Box>
                            {/* Keep raw m_offset for advanced edits */}
                            <TextField
                              label="m_offset"
                              type="number"
                              value={t.m_offset}
                              onChange={(e) => updateTimetable(idx, { m_offset: parseInt(e.target.value || '0', 10) })}
                              size="small"
                              sx={{ width: 120, ml: 'auto' }}
                            />
                          </Box>
                        );
                      });
                    })()}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Note: This is a minimal editor. Netatmo requires the schedule to include all rooms, and mandatory zones 0 (Confort), 1 (Night), and 4 (Eco).
                  </Typography>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEditingSchedule(null)}>Cancel</Button>
              <Button variant="contained" onClick={submitEditSchedule}>Save</Button>
            </DialogActions>
          </Dialog>
        </CardContent>
      </Card>

      {readings.length === 0 ? null : (
        <List sx={{ p: 0 }}>
          {readings.map((reading) => (
            <Card key={reading.id} sx={{ mb: 2 }}>
              <ListItem
                sx={{ py: 2 }}
                secondaryAction={
                  <IconButton 
                    edge="end" 
                    onClick={() => handleDeleteReading(reading.id)}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                }
              >
                <Box sx={{ width: '100%' }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Box display="flex" gap={1}>
                      <Chip
                        icon={<TempIcon />}
                        label={formatTemperature(reading.temperature)}
                        color="primary"
                        size="small"
                      />
                      {reading.humidity && (
                        <Chip
                          icon={<HumidityIcon />}
                          label={formatHumidity(reading.humidity)}
                          color="info"
                          size="small"
                        />
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {formatTimestamp(reading.timestamp)}
                    </Typography>
                  </Box>
                  
                  {reading.location && (
                    <Typography variant="body2" color="text.secondary">
                      📍 {reading.location}
                    </Typography>
                  )}
                  
                  {reading.deviceId && (
                    <Typography variant="caption" color="text.secondary">
                      Device: {reading.deviceId}
                    </Typography>
                  )}
                </Box>
              </ListItem>
            </Card>
          ))}
        </List>
      )}

      {/* FAB removed: Add Reading page no longer used */}
    </Container>
  );
};

export default Dashboard;